import http from '../../core/services/HttpService';

export async function importAbsences(fileOrRows: File | { rows: any[] }, dryRun: boolean) {
  const url = `/api/absences/import?dry_run=${dryRun ? 'true' : 'false'}`;
  if (typeof File !== 'undefined' && fileOrRows instanceof File) {
    const form = new FormData();
    form.append('file', fileOrRows, fileOrRows.name);
    const { data } = await http.post(url, form);
    return data;
  }
  const { data } = await http.post(url, fileOrRows);
  return data;
}

export type ImportAbsencesPreviewRow = {
  action?: 'create' | 'update' | 'error' | string;
  employee?: number | string;
  employee_name?: string;
  absence_type?: string;
  start_datetime?: string;
  end_datetime?: string;
  error?: string;
  [key: string]: any;
};

export type TemplateOpts = {
  format?: 'xlsx' | 'csv';
  prefill?: 'none' | 'all' | 'active' | 'selected';
  work_area?: number | string;
  group?: string; // alias payroll_group
  payroll_group?: string;
  examples?: boolean;
  delimiter?: 'semicolon' | 'comma';
  employee_ids?: (number | string)[];
  // Backwards-compatible keys (will be mapped):
  workAreaId?: number | string;
  payrollGroup?: string;
};

export type AbsencesTemplateOpts = TemplateOpts; // alias for backwards compatibility

function buildQuery(params: Record<string, any>): string {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null || v === '') return;
    if (Array.isArray(v)) q.append(k, v.join(','));
    else q.append(k, String(v));
  });
  const s = q.toString();
  return s ? `?${s}` : '';
}

function inferFilename(headers: Record<string, any>, fallback: string): string {
  const cd = headers?.['content-disposition'] || headers?.['Content-Disposition'];
  if (cd && /filename=/.test(cd)) {
    const m = cd.match(/filename\*?=(?:UTF-8''|")?([^";]+)"?/i);
    if (m && m[1]) return decodeURIComponent(m[1]);
  }
  return fallback;
}

export async function downloadAbsencesTemplate(opts: TemplateOpts = {}): Promise<void> {
  // normalize and map legacy option keys
  const format = opts.format === 'xlsx' ? 'xlsx' : 'csv';
  const params: Record<string, any> = {
    format,
    prefill: opts.prefill ?? 'none',
    work_area: opts.work_area ?? opts.workAreaId,
    payroll_group: opts.payroll_group ?? opts.group ?? opts.payrollGroup,
    examples: opts.examples !== false, // default true
    delimiter: opts.delimiter ?? (format === 'csv' ? 'semicolon' : undefined),
    employee_ids: opts.employee_ids,
  };
  const url = `/api/absences/import/template/${buildQuery(params)}`;
  try {
    const res = await http.get(url, { responseType: 'blob' as any });
    const headers = (res as any)?.headers || {};
    const contentType = headers['content-type'] || headers['Content-Type'] || '';
    const fallbackName =
      format === 'xlsx' || /spreadsheetml/.test(contentType)
        ? 'absences_import_template.xlsx'
        : 'absences_import_template.csv';
    const fileName = inferFilename(headers, fallbackName);
    const blob: Blob = (res as any)?.data instanceof Blob
      ? (res as any).data
      : new Blob([(res as any)?.data], {
          type: contentType || (format === 'xlsx'
            ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            : 'text/csv;charset=utf-8')
        });
    const link = document.createElement('a');
    const objUrl = URL.createObjectURL(blob);
    link.href = objUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(objUrl);
  } catch (e) {
    // Rethrow – UI zeigt Hinweis und nutzt optional lokalen Fallback (XLSX/CSV)
    throw e;
  }
}

// Frontend-generated XLSX template (for consistent Excel download)
let __exceljsPromiseAbs: Promise<typeof import('exceljs')> | null = null;
function getExcelJSAbs() {
  if (!__exceljsPromiseAbs) __exceljsPromiseAbs = import('exceljs');
  return __exceljsPromiseAbs;
}

export async function downloadAbsencesTemplateXlsx(): Promise<void> {
  const ExcelJS = await getExcelJSAbs();
  const wb = new ExcelJS.Workbook();
  const header = ['employee_id','employee_email','datev_personalnr','type','from','to','status','full_day','note','_skip'];
  const demoRows = [
    ['', 'demo.user@example.com', '', 'HOLIDAY', '2025-10-15', '2025-10-17', 'APPROVED', true,  'Beispiel: Urlaub', true],
    ['', '', '11042', 'SICK',    '2025-10-01', '2025-10-03', 'APPROVED', true,  'Beispiel: Krank', true],
    ['', 'demo.user@example.com', '', 'OVERTIME','2025-10-05','2025-10-05','APPROVED', true,  'Beispiel: Überstundenabbau', true],
    ['', '', '11043', 'SICK_CHILD','2025-10-10','2025-10-10','APPROVED', true, 'Beispiel: Kind krank', true],
    ['', 'demo.user@example.com', '', 'EXAM',   '2025-10-12', '2025-10-12', 'APPROVED', false, 'Beispiel: Prüfung 09:00–13:00 (Teil-Tag)', true],
    ['', 'demo.user@example.com', '', 'WISH_OFF','2025-10-20','2025-10-20','APPROVED', false,'Beispiel: Wunschfrei 18:00–21:00', true],
  ];
  const ws = wb.addWorksheet('Absences');
  ws.addRows([header, ...demoRows]);
  const typesHeader = ['Code', 'Deutsch'];
  const typesRows = [
    ['HOLIDAY', 'Urlaub'],
    ['OVERTIME', 'Überstundenabbau'],
    ['SICK', 'Krank'],
    ['SICK_CHILD', 'Kind krank'],
    ['EXAM', 'Prüfung'],
    ['WISH_OFF', 'Wunschfrei'],
  ];
  const ws2 = wb.addWorksheet('Types');
  ws2.addRows([typesHeader, ...typesRows]);
  const buf = await wb.xlsx.writeBuffer();
  const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'absences_import_template.xlsx';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export async function downloadAbsencesTemplateLocalFallback(format: 'xlsx' | 'csv'): Promise<void> {
  if (format === 'xlsx') {
    await downloadAbsencesTemplateXlsx();
    return;
  }
  // CSV (DE) mit Demozeilen + _skip
  const header = 'employee_id;employee_email;datev_personalnr;type;from;to;status;full_day;note;_skip';
  const demo = [
    ['', 'demo.user@example.com', '', 'HOLIDAY', '2025-10-15', '2025-10-17', 'APPROVED', 'TRUE', 'Beispiel: Urlaub', 'TRUE'],
    ['', '', '11042', 'SICK', '2025-10-01', '2025-10-03', 'APPROVED', 'TRUE', 'Beispiel: Krank', 'TRUE'],
    ['', 'demo.user@example.com', '', 'OVERTIME', '2025-10-05', '2025-10-05', 'APPROVED', 'TRUE', 'Beispiel: Überstundenabbau', 'TRUE'],
    ['', '', '11043', 'SICK_CHILD', '2025-10-10', '2025-10-10', 'APPROVED', 'TRUE', 'Beispiel: Kind krank', 'TRUE'],
    ['', 'demo.user@example.com', '', 'EXAM', '2025-10-12', '2025-10-12', 'APPROVED', 'FALSE', 'Beispiel: Prüfung 09:00–13:00 (Teil-Tag)', 'TRUE'],
    ['', 'demo.user@example.com', '', 'WISH_OFF', '2025-10-20', '2025-10-20', 'APPROVED', 'FALSE', 'Beispiel: Wunschfrei 18:00–21:00', 'TRUE'],
  ];
  const csv = [header, ...demo.map((r) => r.join(';'))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const href = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = href;
  a.download = 'absences_import_template.csv';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(href);
}

type TemplateOptions = {
  format?: 'xlsx' | 'csv';
  prefill?: 'none' | 'all' | 'active' | 'selected';
  workAreaId?: number | string | null;
  payrollGroup?: string | null;
  selectedEmployeeIds?: Array<number | string>;
};

export async function downloadAbsencesTemplateDynamic(opts: TemplateOptions = {}): Promise<void> {
  const format = opts.format || 'xlsx';
  const prefill = opts.prefill || 'none';
  const params: Record<string, string> = { format, prefill };
  if (opts.workAreaId) params['work_area'] = String(opts.workAreaId);
  if (opts.payrollGroup) params['group'] = String(opts.payrollGroup);
  if (opts.prefill === 'selected' && Array.isArray(opts.selectedEmployeeIds) && opts.selectedEmployeeIds.length > 0) {
    params['selected_ids'] = opts.selectedEmployeeIds.map(String).join(',');
  }
  const usp = new URLSearchParams(params).toString();
  const url = `/api/absences/import/template/?${usp}`;
  try {
    const res = await http.get(url, { responseType: 'blob' as any });
    const data: any = (res as any)?.data;
    const blob = data instanceof Blob ? data : new Blob([data], { type: format === 'xlsx' ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' : 'text/csv;charset=utf-8' });
    const href = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = href;
    a.download = `absences_import_template.${format}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(href);
  } catch (e: any) {
    // Fallback: if XLSX was requested, generate XLSX locally; otherwise static CSV
    if (format === 'xlsx') {
      await downloadAbsencesTemplateXlsx();
    } else {
      // Generate CSV with demo rows locally to ensure _skip support
      const header = 'employee_id;employee_email;datev_personalnr;type;from;to;status;full_day;note;_skip';
      const demo = [
        ['','demo.user@example.com','', 'HOLIDAY', '2025-10-15', '2025-10-17', 'APPROVED', 'TRUE',  'Beispiel: Urlaub', 'TRUE'],
        ['','',              '11042','SICK',    '2025-10-01', '2025-10-03', 'APPROVED', 'TRUE',  'Beispiel: Krank', 'TRUE'],
        ['','demo.user@example.com','', 'OVERTIME','2025-10-05','2025-10-05','APPROVED','TRUE',  'Beispiel: Überstundenabbau','TRUE'],
        ['','',              '11043','SICK_CHILD','2025-10-10','2025-10-10','APPROVED','TRUE',  'Beispiel: Kind krank','TRUE'],
        ['','demo.user@example.com','', 'EXAM',   '2025-10-12', '2025-10-12', 'APPROVED', 'FALSE', 'Beispiel: Prüfung 09:00–13:00 (Teil-Tag)','TRUE'],
        ['','demo.user@example.com','', 'WISH_OFF','2025-10-20','2025-10-20','APPROVED','FALSE','Beispiel: Wunschfrei 18:00–21:00','TRUE'],
      ];
      const csv = [header, ...demo.map(r => r.join(';'))].join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
      const href = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = href;
      a.download = 'absences_import_template.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(href);
    }
  }
}


